import React from "react";
import { Card, CardBody, Spinner } from "reactstrap";
import { useTopNetworks } from "../../Hooks/useTopNetworks";
import TopNetworksModal from "./TopNetworksModal";
import { formatPeriodRange } from "../../utils/date";

const VISIBLE_ROWS = 5;

const formatPoints = (points) =>
    new Intl.NumberFormat("es-CO").format(Math.round(points || 0));

/**
 * Un corte puede liquidar en varias monedas. Se muestran todas para no dar a
 * entender que el total está en COP cuando hay USD o EUR de por medio.
 */
const formatMoney = (valueByCurrency) => {
    const entries = Object.entries(valueByCurrency || {}).filter(
        ([, data]) => (data?.value || 0) > 0
    );

    if (entries.length === 0) return "—";

    return entries
        .map(
            ([currency, data]) =>
                `${currency} ${new Intl.NumberFormat("es-CO").format(Math.round(data.value))}`
        )
        .join(" · ");
};

const TopNetworksCard = ({ currency, settlementId,province,city }) => {

    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const { networks, settlement, isLoading, error } = useTopNetworks({
        currency,
        settlementId,
        limit: 10,
        province,
        city
    });

    const visible = networks.slice(0, VISIBLE_ROWS);

    const gridColumns = "35px 2fr 1.4fr .8fr 1fr";

    // w-100: la columna es d-flex, y sin esto la tarjeta se encoge al ancho de
    // su contenido y deja un vacío al lado.
    return (

        <Card className="border-0 shadow-sm w-100">

            <CardBody className="pt-2">

                <div className="mb-4">

                    <h5
                        className="fw-bold mb-0"
                        style={{
                            color: "#101828",
                            fontSize: 16
                        }}
                    >
                        Top 10 Redes Más Activas
                    </h5>

                    <small
                        style={{
                            color: "#98A2B3"
                        }}
                    >
                        {
                            // Sin settlementId el dato sale del caché del
                            // dashboard, que es el mes EN CURSO y todavía no
                            // está liquidado. Llamarlo "corte" haría pensar
                            // que son cifras cerradas.
                            formatPeriodRange(settlement?.period)
                                ? `${settlement?.source === "DASHBOARD_CACHE" ? "Mes en curso" : "Corte"} ${formatPeriodRange(settlement.period)}`
                                : "Mes en curso"
                        }
                    </small>

                </div>

                <div
                    className="d-grid pb-3"
                    style={{
                        gridTemplateColumns: gridColumns,
                        fontSize: 12,
                        color: "#98A2B3",
                        fontWeight: 600,
                        borderBottom: "1px solid #F2F4F7"
                    }}
                >

                    <div></div>

                    <div>Distribuidor</div>

                    <div className="text-end">
                        Regalía Liquidada
                    </div>

                    <div className="text-center">
                        Usuarios
                    </div>

                    <div className="text-end">
                        Puntos
                    </div>

                </div>

                {
                    isLoading && (
                        <div className="text-center py-5">
                            <Spinner size="sm" />
                        </div>
                    )
                }

                {
                    !isLoading && error && (
                        <div className="py-4" style={{ color: "#667085", fontSize: 13 }}>
                            No se pudieron cargar las redes.
                        </div>
                    )
                }

                {
                    !isLoading && !error && visible.length === 0 && (
                        <div className="py-4" style={{ color: "#667085", fontSize: 13 }}>
                            El último corte no tiene redes con puntos.
                        </div>
                    )
                }

                {

                    !isLoading && !error && visible.map((network, index) => (

                        <div
                            key={network.user_id}
                            className="d-grid align-items-center"
                            style={{
                                gridTemplateColumns: gridColumns,
                                minHeight: 60,
                                borderBottom: "1px solid #F2F4F7"
                            }}
                        >

                            <div
                                style={{
                                    color: "#667085",
                                    fontWeight: 600,
                                    fontSize: 14
                                }}
                            >
                                {index + 1}
                            </div>

                            <div
                                style={{
                                    color: "#344054",
                                    fontWeight: 500,
                                    fontSize: 14,
                                    lineHeight: "18px"
                                }}
                            >
                                {network.user?.name || "—"}
                            </div>

                            <div
                                className="text-end"
                                style={{
                                    color: "#667085",
                                    fontSize: 13
                                }}
                            >
                                {formatMoney(network.value_by_currency)}
                            </div>

                            <div
                                className="text-center"
                                style={{
                                    color: "#344054",
                                    fontSize: 13
                                }}
                            >
                                {network.network_users_count}
                            </div>

                            <div
                                className="text-end"
                                style={{
                                    color: "#12B76A",
                                    fontWeight: 700,
                                    fontSize: 13
                                }}
                            >
                                {formatPoints(network.total_points)}
                            </div>

                        </div>

                    ))

                }

                <div className="text-center mt-4">

                    <button
                        type="button"
                        className="btn btn-link p-0"
                        disabled={networks.length === 0}
                        onClick={() => setIsModalOpen(true)}
                        style={{
                            textDecoration: "none",
                            color: "#4F46E5",
                            fontWeight: 600,
                            fontSize: 14
                        }}
                    >
                        Ver las 10 redes completas
                    </button>

                </div>

            </CardBody>

            <TopNetworksModal
                isOpen={isModalOpen}
                toggle={() => setIsModalOpen(false)}
                networks={networks}
                settlement={settlement}
                formatPoints={formatPoints}
                formatMoney={formatMoney}
            />

        </Card>

    );

};

export default TopNetworksCard;
